import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ROUTES } from "constants/routes-path";
import {
    Button,
    Divider,
    Flex,
    Grid,
    GridItem,
    Stack,
    Tab,
    Tabs,
    TabList,
    TabPanels,
    TabPanel,
    Text,
    Tooltip,
    useColorModeValue,
    Box,
    Wrap,
    WrapItem,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
    ModalCloseButton
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { trafficViolationService, ViolationSituation, ViolationStatus } from "services/trafficViolationService";
import { processesService } from "services/processesService";
import TrafficViolationTimeline from "./TrafficViolationTimeline";
import { normalizeImagePath, isRemoteUrl, MEASUREMENT_EQUIPMENT_TYPES } from "utils/traffic-violation-utils";
import { BiPrinter, BiHistory } from "react-icons/bi";
import {
    FaCheckCircle,
    FaCreditCard,
    FaTimesCircle
} from "react-icons/fa";
import { currentUserHasPermission } from "utils/permission";
import Loading from "../../../components/Loading/Loading";
const { PERMISSIONS } = require("constants/permission");
import { AuthContext } from 'contexts/AuthContext';


function TrafficViolationDetails() {
    const { id } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const showToast = useCustomToast();
    const textColor = useColorModeValue("gray.700", "white");
    const cardBg = useColorModeValue("gray.50", "gray.700");
    const [trafficViolationData, setTrafficViolationData] = useState([]);
    const [processos, setProcessos] = useState([]);
    const [isLoadingPrintAit, setIsLoadingPrintAit] = useState(false);
    const [isLoadingPrintNa, setIsLoadingPrintNa] = useState(false);
    const [isLoadingPrintNp, setIsLoadingPrintNp] = useState(false);
    const { getUserData } = useContext(AuthContext);
    const user = getUserData();
    const [imagePreviewUrl, setImagePreviewUrl] = useState(null);

    useEffect(() => {
        if (id) {
            fetchTrafficViolation(id);
        }
    }, [id]);

    const fetchTrafficViolation = async (id) => {
        try {
            const [trafficViolation, processosData] = await Promise.all([
                trafficViolationService.details(id),
                processesService.listByInfractionId(id).catch(() => []),
            ]);
            setProcessos(processosData);
            if (trafficViolation?.images?.length) {
                const imageUrls = await Promise.all(
                trafficViolation.images.map(async (img) => {
                    if (isRemoteUrl(img)) {
                        return img;
                    }
                    const fileName = normalizeImagePath(img);
                    return await trafficViolationService.getImage(id, fileName);
                })
            );
                setTrafficViolationData({
                    ...trafficViolation,
                    imageUrls,
                });
            } else {
                setTrafficViolationData(trafficViolation);
            }
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    };

    const handlePrintAit = async (id) => {
        try {
            setIsLoadingPrintAit(true);

            const {data, headers} = await trafficViolationService.printAit(id);
            const fileName = headers['content-disposition'].match(/filename="(.+)"/)[1];

            const url = URL.createObjectURL(new Blob([data]));
            Object.assign(document.createElement('a'), {
                href: url,
                download: fileName
            }).click();

            URL.revokeObjectURL(url);
        } catch (error) {
            showToast({ title: t(error), status: "error" });
        } finally {
            setIsLoadingPrintAit(false);
        }
    };

    const handlePrintNa = async (id) => {
        try {
            setIsLoadingPrintNa(true);

            const {data, headers} = await trafficViolationService.printNa(id);
            const fileName = headers['content-disposition'].match(/filename="(.+)"/)[1];

            const url = URL.createObjectURL(new Blob([data]));
            Object.assign(document.createElement('a'), {
                href: url,
                download: fileName
            }).click();

            URL.revokeObjectURL(url);
        } catch (error) {
            showToast({ title: t(error), status: "error" });
        } finally {
            setIsLoadingPrintNa(false);
        }
    };

    const handlePrintNp = async (id) => {
        try {
            setIsLoadingPrintNp(true);

            const {data, headers} = await trafficViolationService.printNp(id);
            const fileName = headers['content-disposition'].match(/filename="(.+)"/)[1];

            const url = URL.createObjectURL(new Blob([data]));
            Object.assign(document.createElement('a'), {
                href: url,
                download: fileName
            }).click();

            URL.revokeObjectURL(url);
        } catch (error) {
            showToast({ title: t(error), status: "error" });
        } finally {
            setIsLoadingPrintNp(false);
        }
    };

        const handlePrintNoRecord = async (trafficViolation) => {
            try {
                const url = ROUTES.getNoRecordPath.replace(":plate", trafficViolation.placa_veiculo);
                window.open(url, '_blank');
            } catch (err) {
                showToast({ title: t(err), status: "error" });
            }
        }
    
        const handlePrintSne = async (trafficViolation) => {
            try {
                const url = ROUTES.getSnePath.replace(":plate", trafficViolation.placa_veiculo);
                window.open(url, '_blank');
            } catch (err) {
                showToast({ title: t(err), status: "error" });
            }
        }

        const handleQueryHistory = async (trafficViolation) => {
            try {
                const { data } = await trafficViolationService.queryTrafficViolationHistory(trafficViolation.numero_controle_detran);
                showToast({ title: t("traffic-violation.messages.success.update-history"), status: "success" });
            } catch (err) {
                showToast({ title: t(err), status: "error" });
            }
        }

    const showPrintNa = (
        trafficViolationData.situacao === t(`traffic-violation.labels.situation.${ViolationSituation.NA_GENERATED.labelKey}`) &&
        currentUserHasPermission(PERMISSIONS.VIOLATION_QUERY)
    )
    const showPrintNp = (
        trafficViolationData.situacao === t(`traffic-violation.labels.situation.${ViolationSituation.NP_GENERATED.labelKey}`) &&
        currentUserHasPermission(PERMISSIONS.VIOLATION_QUERY)
    )
    const showPrintAit = (
        (trafficViolationData.status === ViolationStatus.VALIDATED.value ||
        trafficViolationData.status === ViolationStatus.NOT_VALIDATED.value) &&
        currentUserHasPermission(PERMISSIONS.AGENT_VIOLATION_QUERY) &&
        currentUserHasPermission(PERMISSIONS.PRINT_AIT)
    )
    const showCancel = (
        (trafficViolationData.situacao === t(`traffic-violation.labels.situation.${ViolationSituation.NA_GENERATED.labelKey}`) ||
        trafficViolationData.situacao === t(`traffic-violation.labels.situation.${ViolationSituation.NP_GENERATED.labelKey}`)) &&
        currentUserHasPermission(PERMISSIONS.VIOLATION_CANCEL)
    )
    const showDefer = (
        (trafficViolationData.situacao === t(`traffic-violation.labels.situation.${ViolationSituation.NA_GENERATED.labelKey}`) ||
        trafficViolationData.situacao === t(`traffic-violation.labels.situation.${ViolationSituation.NP_GENERATED.labelKey}`)) &&
        currentUserHasPermission(PERMISSIONS.VIOLATION_APPROVE)
    )
    const showQueryHistory = (
        currentUserHasPermission(PERMISSIONS.ADMINISTRATOR) || (Array.isArray(user.perfis) && user.perfis.some(x => x.nome === "Administrador"))
    )

    return (
        <Flex direction="column" pt={{ sm: "125px", lg: "75px" }}>
            <Card px="0px" mb="24px">
                <CardHeader px="22px" mb="24px">
                    <Flex direction="column">
                        <Text color={textColor} fontSize="lg" mb="6px">
                            <Text as="span" fontWeight="normal">
                                {t("traffic-violation.details.title")}
                            </Text>{"\u00A0"}
                            <Text as="span" fontWeight="bold">
                                {trafficViolationData && trafficViolationData.numero_ait ? trafficViolationData.numero_ait : ""}
                            </Text>
                        </Text>
                    </Flex>
                </CardHeader>
                <CardBody px="22px">
                    {
                        trafficViolationData ? (
                            <>
                                <Flex justify="center" mb={{ base: 4, md: 6 }}>
                                    <Wrap justify="center" spacing="2">
                                    {showPrintAit && (
                                        <WrapItem>
                                            <Button
                                                size={{ base: "xs", md: "sm" }} 
                                                variant="light"
                                                fontSize="xs"
                                                leftIcon={<BiPrinter/>}
                                                onClick={() => handlePrintAit(id)}
                                            >
                                                {t("traffic-violation.list.table.actions.print-ait")}
                                            </Button>
                                        </WrapItem>
                                    )}
                                    {showPrintNa && (
                                        <WrapItem>
                                            <Button
                                                size={{ base: "xs", md: "sm" }} 
                                                variant="light"
                                                fontSize="xs"
                                                leftIcon={<BiPrinter/>}
                                                onClick={() => handlePrintNa(id)}
                                            >
                                                {t("traffic-violation.list.table.actions.print-na")}
                                            </Button>
                                        </WrapItem>
                                    )}
                                    {showPrintNp && (
                                        <WrapItem>
                                            <Button
                                                size={{ base: "xs", md: "sm" }} 
                                                variant="light"
                                                fontSize="xs"
                                                leftIcon={<BiPrinter/>}
                                                onClick={() => handlePrintNp(id)}
                                            >
                                                {t("traffic-violation.list.table.actions.print-np")}
                                            </Button>
                                        </WrapItem>
                                    )}
                                    <WrapItem>
                                        <Button
                                            size={{ base: "xs", md: "sm" }} 
                                            variant="light"
                                            fontSize="xs"
                                            leftIcon={<FaCreditCard/>}
                                                onClick={() => navigate(ROUTES.transportManagementPath + ROUTES.trafficViolations + ROUTES.trafficViolationsConfirmPayment.replace(":id", id))}
                                        >
                                            {t("traffic-violation.list.table.actions.confirm-payment")}
                                        </Button>
                                    </WrapItem>
                                    {showCancel && (
                                        <WrapItem>
                                            <Button
                                                size={{ base: "xs", md: "sm" }} 
                                                variant="light"
                                                fontSize="xs"
                                                leftIcon={<FaTimesCircle/>}
                                                onClick={() => navigate(ROUTES.trafficViolationsCancelPath.replace(":id", id))}
                                            >
                                                {t("traffic-violation.list.table.actions.cancel")}
                                            </Button>
                                        </WrapItem>
                                    )}
                                    {showDefer && (
                                        <WrapItem>
                                            <Button
                                                size={{ base: "xs", md: "sm" }} 
                                                variant="light"
                                                fontSize="xs"
                                                leftIcon={<FaCheckCircle/>}
                                                onClick={() => navigate(ROUTES.trafficViolationsDeferPath.replace(":id", id))}
                                            >
                                                {t("traffic-violation.list.table.actions.defer")}
                                            </Button>
                                        </WrapItem>
                                    )}
                                    <WrapItem>
                                        <Button
                                            size={{ base: "xs", md: "sm" }} 
                                            variant="light"
                                            fontSize="xs"
                                            leftIcon={<BiPrinter/>}
                                            onClick={() => handlePrintNoRecord(trafficViolationData)}
                                        >
                                            {t("traffic-violation.list.table.actions.print-no-record")}
                                        </Button>
                                    </WrapItem>
                                    <WrapItem>
                                        <Button
                                            size={{ base: "xs", md: "sm" }} 
                                            variant="light"
                                            fontSize="xs"
                                            leftIcon={<BiPrinter/>}
                                            onClick={() => handlePrintSne(trafficViolationData)}
                                        >
                                            {t("traffic-violation.list.table.actions.print-sne-status")}
                                        </Button>
                                    </WrapItem>
                                    <pre>{showQueryHistory}</pre>
                                        {trafficViolationData.numero_controle_detran && showQueryHistory && (
                                            <WrapItem>
                                                <Button
                                                    size={{ base: "xs", md: "sm" }}
                                                    variant="light"
                                                    fontSize="xs"
                                                    leftIcon={<BiHistory />}
                                                    onClick={() => handleQueryHistory(trafficViolationData)}
                                                >
                                                    {t("traffic-violation.list.table.actions.query-history")}
                                                </Button>
                                            </WrapItem>
                                        )}
                                    </Wrap> 
                                </Flex>
                                <Tabs variant="enclosed">
                                    <TabList>
                                        <Tab>{t("traffic-violation.details.tab-general.title")}</Tab>
                                        <Tab>{t("traffic-violation.details.tab-timeline.title")}</Tab>
                                        {trafficViolationData.nome_condutor && (
                                            <Tab>{t("traffic-violation.details.tab-real-infractor.title")}</Tab>
                                        )}
                                        {trafficViolationData.tipo_equipamento_afericao === MEASUREMENT_EQUIPMENT_TYPES.ETILOMETRO && (
                                            <Tab>{t("traffic-violation.details.tab-breathalyzer-test.title")}</Tab>
                                        )}
                                        {trafficViolationData?.images?.length > 0 && (
                                            <Tab>{t("traffic-violation.details.tab-images.title")}</Tab>
                                        )}
                                    </TabList>
                                    <TabPanels>
                                        <TabPanel>
                                            <Grid
                                                templateColumns={{ base: "1fr", md: "1fr 1fr" }}
                                                gap="30px"
                                            >
                                                {/* AIT Section */}
                                                <Card border="1px solid #3884cc" bg={cardBg}>
                                                    <CardHeader>
                                                        <Text color={textColor} fontSize="md" fontWeight="bold">
                                                            {t("traffic-violation.details.tab-general.ait-section.title")}
                                                        </Text>
                                                    </CardHeader>
                                                    <Divider borderColor="#3884cc" mb="20px" />
                                                    <CardBody>
                                                        <Stack spacing={4}>
                                                            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap="6px">
                                                                <GridItem color={textColor} fontSize="md" minW={0}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.ait-section.ait-number")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.numero_ait ?? "-"}
                                                                </GridItem>
                                                                <GridItem color={textColor} fontSize="md" minW={0}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.ait-section.status")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.situacao ?? "-"}
                                                                </GridItem>
                                                                <GridItem color={textColor} fontSize="md" minW={0}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.ait-section.batch")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData?.nome_lote ?? "-"}
                                                                </GridItem>
                                                                <GridItem color={textColor} fontSize="md" minW={0}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.ait-section.renainf-code")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.codigo_renainf ?? "-"}
                                                                </GridItem>
                                                            </Grid>
                                                        </Stack>
                                                    </CardBody>
                                                </Card>

                                                {/* Vehicle Section */}
                                                <Card border="1px solid #3884cc" bg={cardBg}>
                                                    <CardHeader>
                                                        <Text color={textColor} fontSize="md" fontWeight="bold">
                                                            {t("traffic-violation.details.tab-general.vehicle-section.title")}
                                                        </Text>
                                                    </CardHeader>
                                                    <Divider borderColor="#3884cc" mb="20px" />
                                                    <CardBody>
                                                        <Stack spacing={4}>
                                                            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap="6px">
                                                                <GridItem fontSize="md" minW={0}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.vehicle-section.plate")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.placa_veiculo ?? "-"}
                                                                </GridItem>
                                                                <GridItem fontSize="md" minW={0}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.vehicle-section.uf")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.uf_placa_veiculo ?? "-"}
                                                                </GridItem>
                                                                <GridItem fontSize="md" minW={0}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.vehicle-section.category")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.descricao_categoria_veiculo ?? "-"}
                                                                </GridItem>
                                                            </Grid>

                                                            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap="6px">
                                                                <GridItem fontSize="md" minW={0}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.vehicle-section.renavam")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.numero_renavam_veiculo ?? "-"}
                                                                </GridItem>
                                                                <GridItem fontSize="md" minW={0}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.vehicle-section.brand")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.descricao_marca_modelo_veiculo ?? "-"}
                                                                </GridItem>
                                                            </Grid>

                                                            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap="6px">
                                                                <GridItem fontSize="md" minW={0}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.vehicle-section.color")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.descricao_cor_veiculo ?? "-"}
                                                                </GridItem>
                                                                <GridItem fontSize="md" minW={0}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.vehicle-section.type")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.descricao_tipo_veiculo ?? "-"}
                                                                </GridItem>
                                                            </Grid>
                                                        </Stack>
                                                    </CardBody>
                                                </Card>

                                                {/* Owner Section */}
                                                <Card border="1px solid #3884cc" bg={cardBg}>
                                                    <CardHeader>
                                                        <Text color={textColor} fontSize="md" fontWeight="bold">
                                                            {t("traffic-violation.details.tab-general.owner-section.title")}
                                                        </Text>
                                                    </CardHeader>
                                                    <Divider borderColor="#3884cc" mb="20px" />
                                                    <CardBody>
                                                        <Stack spacing={4}>
                                                            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap="6px">
                                                                <GridItem fontSize="md" color={textColor} minW={0}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.owner-section.name")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.nome_proprietario_veiculo_ait ?? "-"}
                                                                </GridItem>
                                                                <GridItem fontSize="md" color={textColor} minW={0}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.owner-section.cpf-cnpj")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData?.cpf_cnpj_proprietario_veiculo_ait ?? "-"}
                                                                </GridItem>
                                                                <GridItem fontSize="md" color={textColor} minW={0}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.owner-section.cnh")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.cnh_proprietario_veiculo ?? "-"}
                                                                </GridItem>
                                                                
                                                            </Grid>

                                                            <Grid gap="6px">
                                                                <GridItem fontSize="md" color={textColor} minW={0}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.owner-section.address")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.logradouro_proprietario_veiculo_ait ?? "-"}
                                                                </GridItem>
                                                            </Grid>

                                                            <Grid gap="6px">
                                                                <GridItem fontSize="md" color={textColor} minW={0}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.owner-section.complement")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.complemento_imovel_proprietario_veiculo_ait ?? "-"}
                                                                </GridItem>
                                                                
                                                            </Grid>

                                                            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap="6px">
                                                                <GridItem fontSize="md" color={textColor} minW={0}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.owner-section.city")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.nome_municipio_imovel_proprietario_veiculo_ait ?? "-"}
                                                                </GridItem>
                                                                <GridItem fontSize="md" color={textColor} minW={0}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.owner-section.uf")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.uf_imovel_proprietario_veiculo_ait ?? "-"}
                                                                </GridItem>
                                                                <GridItem fontSize="md" color={textColor} minW={0}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.owner-section.cep")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.cep_imovel_proprietario_veiculo_ait ?? "-"}
                                                                </GridItem>
                                                            </Grid>
                                                        </Stack>
                                                    </CardBody>
                                                </Card>

                                                {/* AIT Type Section */}
                                                <Card border="1px solid #3884cc" bg={cardBg}>
                                                    <CardHeader>
                                                        <Text color={textColor} fontSize="md" fontWeight="bold">
                                                            {t("traffic-violation.details.tab-general.ait-type-section.title")}
                                                        </Text>
                                                    </CardHeader>
                                                    <Divider borderColor="#3884cc" mb="20px" />
                                                    <CardBody>
                                                        <Stack spacing={4}>
                                                            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap="6px">
                                                                <GridItem fontSize="md" color={textColor} minW={0}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.ait-type-section.code")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.codigo_tipo_infracao ?? "-"}
                                                                    <Text as="span" fontWeight="bold" ml="4px">
                                                                        {t("traffic-violation.details.tab-general.ait-type-section.unfolding")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.desdobramento_tipo_infracao ?? "-"}
                                                                </GridItem>
                                                                <GridItem fontSize="md" color={textColor} minW={0}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.ait-type-section.severity")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.gravidade_tipo_infracao ?? "-"}
                                                                </GridItem>
                                                                <GridItem fontSize="md" color={textColor} minW={0}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.ait-type-section.fine-score")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.pontuacao_tipo_infracao ?? "-"}
                                                                </GridItem>
                                                            </Grid>

                                                            <Grid gap="6px">
                                                                <GridItem minW={0}>
                                                                    <Text fontSize="md" color={textColor}>
                                                                        <Text as="span" fontWeight="bold">
                                                                            {t("traffic-violation.details.tab-general.ait-type-section.description")}:
                                                                        </Text>{"\u00A0"}
                                                                        {trafficViolationData.descricao_tipo_infracao ?? "-"}
                                                                    </Text>
                                                                </GridItem>
                                                            </Grid>

                                                            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap="6px">
                                                                <GridItem fontSize="md" color={textColor} minW={0}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.ait-type-section.value")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData?.valor_tipo_infracao ?? "-"}
                                                                </GridItem>
                                                                <GridItem fontSize="md" color={textColor} minW={0}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.ait-type-section.agent")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.agente_tipo_infracao ?? "-"}
                                                                </GridItem>
                                                                <GridItem fontSize="md" color={textColor} minW={0}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.ait-type-section.ctb-article")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.artigo_ctb_tipo_infracao ?? "-"}
                                                                </GridItem>
                                                            </Grid>
                                                        </Stack>
                                                    </CardBody>
                                                </Card>

                                                {/* Commitment Section */}
                                                <Card border="1px solid #3884cc" bg={cardBg}>
                                                    <CardHeader>
                                                        <Text color={textColor} fontSize="md" fontWeight="bold">
                                                            {t("traffic-violation.details.tab-general.commitment-section.title")}
                                                        </Text>
                                                    </CardHeader>
                                                    <Divider borderColor="#3884cc" mb="20px" />
                                                    <CardBody>
                                                        <Stack spacing={4}>
                                                            <Grid gap="6px">
                                                                <Text fontSize="md" color={textColor}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.commitment-section.address")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.logradouro_local_infracao ?? "-"}
                                                                </Text>
                                                            </Grid>

                                                            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap="6px">
                                                                <GridItem fontSize="md" color={textColor} minW={0}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.commitment-section.complement")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.complemento_local_infracao ?? "-"}
                                                                </GridItem>
                                                                <GridItem fontSize="md" color={textColor} minW={0}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.commitment-section.date-time")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.data_infracao ?? "-"}
                                                                </GridItem>
                                                            </Grid>

                                                            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap="6px">
                                                                <GridItem minW={0} colSpan={trafficViolationData.medidas_administrativas ? 1 : 2}>
                                                                    <Text fontSize="md" color={textColor}>
                                                                        <Text as="span" fontWeight="bold">
                                                                            {t("traffic-violation.details.tab-general.commitment-section.observations")}:
                                                                        </Text>{"\u00A0"}
                                                                        {trafficViolationData.observacoes ?? "-"}
                                                                    </Text>
                                                                </GridItem>

                                                                {trafficViolationData.medidas_administrativas && (
                                                                    <GridItem minW={0}>
                                                                        <Text fontSize="md" color={textColor}>
                                                                            <Text as="span" fontWeight="bold">
                                                                                {t("traffic-violation.details.tab-general.commitment-section.administrative-measures")}:
                                                                            </Text>{"\u00A0"}
                                                                            {trafficViolationData.medidas_administrativas}
                                                                        </Text>
                                                                    </GridItem>
                                                                )}
                                                            </Grid>
                                                        </Stack>
                                                    </CardBody>
                                                </Card>

                                                {/* Payment Section */}
                                                <Card border="1px solid #3884cc" bg={cardBg}>
                                                    <CardHeader>
                                                        <Text color={textColor} fontSize="md" fontWeight="bold">
                                                            {t("traffic-violation.details.tab-general.payment-section.title")}
                                                        </Text>
                                                    </CardHeader>
                                                    <Divider borderColor="#3884cc" mb="20px" />
                                                    <CardBody>
                                                        <Stack spacing={4}>
                                                            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap="6px">
                                                                <Text fontSize="md" color={textColor}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.payment-section.bank-code")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.codigo_banco ?? "-"}
                                                                </Text>
                                                                <Text fontSize="md" color={textColor}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.payment-section.agency-code")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.codigo_agencia ?? "-"}
                                                                </Text>
                                                            </Grid>

                                                            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap="6px">
                                                                <Text fontSize="md" color={textColor}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.payment-section.paid-value")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData?.valor_pago ?? "-"}
                                                                </Text>
                                                                <Text fontSize="md" color={textColor}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.payment-section.payment-date")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.data_pagamento ?? "-"}
                                                                </Text>
                                                                <Text fontSize="md" color={textColor}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.payment-section.credit-date")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.data_credito ?? "-"}
                                                                </Text>
                                                            </Grid>
                                                        </Stack>
                                                    </CardBody>
                                                </Card>

                                                {/* Measurement Section */}
                                                <Card border="1px solid #3884cc" bg={cardBg}>
                                                    <CardHeader>
                                                        <Text color={textColor} fontSize="md" fontWeight="bold">
                                                            {t("traffic-violation.details.tab-general.measurement-section.title")}
                                                        </Text>
                                                    </CardHeader>
                                                    <Divider borderColor="#3884cc" mb="20px" />
                                                    <CardBody>
                                                        <Stack spacing={4}>
                                                            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap="6px">
                                                                <Text fontSize="md" color={textColor}>
                                                                    <Tooltip label={t("traffic-violation.details.tab-general.measurement-section.value")} hasArrow>
                                                                        <Text as="span" fontWeight="bold">
                                                                            {t("traffic-violation.details.tab-general.measurement-section.value-short")}:
                                                                        </Text>
                                                                    </Tooltip>{"\u00A0"}
                                                                    {trafficViolationData.valor_medicao_real ?? "-"}
                                                                </Text>
                                                                <Text fontSize="md" color={textColor}>
                                                                    <Tooltip label={t("traffic-violation.details.tab-general.measurement-section.limit-value-allowed")} hasArrow>
                                                                        <Text as="span" fontWeight="bold">
                                                                            {t("traffic-violation.details.tab-general.measurement-section.limit-value-allowed-short")}:
                                                                        </Text>
                                                                    </Tooltip>{"\u00A0"}
                                                                    {trafficViolationData.valor_limite_permitido ?? "-"}
                                                                </Text>
                                                                <Text fontSize="md" color={textColor}>
                                                                    <Tooltip label={t("traffic-violation.details.tab-general.measurement-section.limit-value-considered")} hasArrow>
                                                                        <Text as="span" fontWeight="bold">
                                                                            {t("traffic-violation.details.tab-general.measurement-section.limit-value-considered-short")}:
                                                                        </Text>
                                                                    </Tooltip>{"\u00A0"}
                                                                    {trafficViolationData.valor_limite_considerado ?? "-"}
                                                                </Text>
                                                            </Grid>
                                                        </Stack>
                                                    </CardBody>
                                                </Card>

                                                {/* SNE Section */}
                                                <Card border="1px solid #3884cc" bg={cardBg}>
                                                    <CardHeader>
                                                        <Text color={textColor} fontSize="md" fontWeight="bold">
                                                            {t("traffic-violation.details.tab-general.sne-section.title")}
                                                        </Text>
                                                    </CardHeader>
                                                    <Divider borderColor="#3884cc" mb="20px" />
                                                    <CardBody>
                                                        <Stack spacing={4}>
                                                            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap="6px">
                                                                <Text fontSize="md" color={textColor}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.sne-section.enrollment-date-at-issuance")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.data_adesao_sne_ait ?? "-"}
                                                                </Text>
                                                                <Text fontSize="md" color={textColor}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.sne-section.enrollment-date-at-infraction")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.data_adesao_sne_autuacao ?? "-"}
                                                                </Text>
                                                                <Text fontSize="md" color={textColor}>
                                                                    <Text as="span" fontWeight="bold">
                                                                        {t("traffic-violation.details.tab-general.sne-section.enrollment-date-at-penalty")}:
                                                                    </Text>{"\u00A0"}
                                                                    {trafficViolationData.data_adesao_sne_penalidade ?? "-"}
                                                                </Text>
                                                            </Grid>
                                                        </Stack>
                                                    </CardBody>
                                                </Card>
                                            </Grid>
                                        </TabPanel>
                                        <TabPanel>
                                            <Card border="1px solid #3884cc" mb="30px" bg={cardBg}>
                                                <TrafficViolationTimeline timelineData={trafficViolationData} processos={processos} />
                                            </Card>
                                        </TabPanel>
                                        {trafficViolationData.nome_condutor && (
                                            <TabPanel>
                                            {/* Real Infractor Section */}
                                            <Card border="1px solid #3884cc" bg={cardBg}>
                                                <CardHeader>
                                                    <Text color={textColor} fontSize="md" fontWeight="bold">
                                                        {t("traffic-violation.details.tab-real-infractor.driver-indicated")}
                                                    </Text>
                                                </CardHeader>
                                                <Divider borderColor="#3884cc" mb="20px" />
                                                <CardBody>
                                                    <Stack spacing={4}>
                                                        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap="6px">
                                                            <Text color={textColor} fontSize="md">
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("traffic-violation.details.tab-real-infractor.name")}:
                                                                </Text>{"\u00A0"}
                                                                {trafficViolationData.nome_condutor ?? "-"}
                                                            </Text>

                                                            <Text color={textColor} fontSize="md">
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("traffic-violation.details.tab-real-infractor.cnh")}:
                                                                </Text>{"\u00A0"}
                                                                {trafficViolationData.cnh_indicacao_condutor ?? "-"}
                                                            </Text>

                                                            <Text color={textColor} fontSize="md">
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("traffic-violation.details.tab-real-infractor.cpf")}:
                                                                </Text>{"\u00A0"}
                                                                {trafficViolationData.documento_condutor ?? "-"}
                                                            </Text>

                                                            <Text color={textColor} fontSize="md">
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("traffic-violation.details.tab-real-infractor.register-number")}:
                                                                </Text>{"\u00A0"}
                                                                {trafficViolationData.numero_registro_indicacao_condutor ?? "-"}
                                                            </Text>

                                                            <Text color={textColor} fontSize="md">
                                                                <Text as="span" fontWeight="bold">
                                                                    {t("traffic-violation.details.tab-real-infractor.process-number")}:
                                                                </Text>{"\u00A0"}
                                                                {trafficViolationData.numero_processo_indicacao_condutor ?? "-"}
                                                            </Text>
                                                        </Grid>
                                                    </Stack>
                                                </CardBody>
                                            </Card>
                                        </TabPanel>
                                        )}
                                        {trafficViolationData.tipo_equipamento_afericao === MEASUREMENT_EQUIPMENT_TYPES.ETILOMETRO && (
                                        <TabPanel>
                                            {/* Breathalyzer Section */}
                                            <Card border="1px solid #3884cc" bg={cardBg}>
                                                <CardHeader>
                                                    <Text color={textColor} fontSize="md" fontWeight="bold">
                                                        {t("traffic-violation.details.tab-breathalyzer-test.title")}
                                                    </Text>
                                                </CardHeader>
                                                <Divider borderColor="#3884cc" mb="20px" />
                                                <CardBody>
                                                    <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap="6px">
                                                        <Text fontSize="md" color={textColor}>
                                                            <Text as="span" fontWeight="bold">
                                                                {t("traffic-violation.details.tab-breathalyzer-test.brand")}:
                                                            </Text>{"\u00A0"}
                                                            {trafficViolationData.marca_equipamento_afericao ?? "-"}
                                                        </Text>
                                                        <Text fontSize="md" color={textColor}>
                                                            <Text as="span" fontWeight="bold">
                                                                {t("traffic-violation.details.tab-breathalyzer-test.model")}:
                                                            </Text>{"\u00A0"}
                                                            {trafficViolationData.modelo_equipamento_afericao ?? "-"}
                                                        </Text>
                                                        <Text fontSize="md" color={textColor}>
                                                            <Text as="span" fontWeight="bold">
                                                                {t("traffic-violation.details.tab-breathalyzer-test.serial-number")}:
                                                            </Text>{"\u00A0"}
                                                            {trafficViolationData.numero_serie_equipamento_afericao ?? "-"}
                                                        </Text>
                                                        <Text fontSize="md" color={textColor}>
                                                            <Text as="span" fontWeight="bold">
                                                                {t("traffic-violation.details.tab-breathalyzer-test.real-measurement")}:
                                                            </Text>{"\u00A0"}
                                                            {trafficViolationData.valor_medicao_real ?? "-"}
                                                        </Text>
                                                        <Text fontSize="md" color={textColor}>
                                                            <Text as="span" fontWeight="bold">
                                                                {t("traffic-violation.details.tab-breathalyzer-test.allowed-limit")}:
                                                            </Text>{"\u00A0"}
                                                            {trafficViolationData.valor_limite_permitido ?? "-"}
                                                        </Text>
                                                        <Text fontSize="md" color={textColor}>
                                                            <Text as="span" fontWeight="bold">
                                                                {t("traffic-violation.details.tab-breathalyzer-test.considered-limit")}:
                                                            </Text>{"\u00A0"}
                                                            {trafficViolationData.valor_limite_considerado ?? "-"}
                                                        </Text>
                                                    </Grid>
                                                </CardBody>
                                            </Card>
                                        </TabPanel>
                                        )}
                                        {/* Foto */}
                                        {trafficViolationData.imageUrls?.length > 0 && (
                                            <TabPanel>
                                                <Card border="1px solid #3884cc" mb="30px" bg={cardBg}>
                                                    <CardHeader>
                                                        <Text color={textColor} fontSize="md" fontWeight="bold">
                                                            {t("traffic-violation.details.tab-images.title")}
                                                        </Text>
                                                    </CardHeader>
                                                    <Divider borderColor="#3884cc" mb="20px" />
                                                    <CardBody>
                                                        <Box
                                                            h={{ base: "62vh", md: "68vh" }}
                                                            overflow="hidden"
                                                        >
                                                            <Grid
                                                                gap="10px"
                                                                h="100%"
                                                                templateColumns={{
                                                                    base: "1fr",
                                                                    md: trafficViolationData.imageUrls?.length === 1
                                                                        ? "1fr"
                                                                        : "repeat(" + trafficViolationData.imageUrls?.length + ", 1fr)"
                                                                }}
                                                                alignItems="center"
                                                            >
                                                                {trafficViolationData.imageUrls?.map((src, idx) => (
                                                                    <Box
                                                                        key={idx}
                                                                        h="100%"
                                                                        overflow="hidden"
                                                                        display="flex"
                                                                        alignItems="center"
                                                                        justifyContent="center"
                                                                        cursor="pointer"
                                                                        onClick={() => setImagePreviewUrl(src)}
                                                                    >
                                                                        <img
                                                                            src={src}
                                                                            alt={`Foto ${idx + 1}`}
                                                                            style={{
                                                                                maxWidth: "100%",
                                                                                maxHeight: "100%",
                                                                                width: "auto",
                                                                                height: "auto",
                                                                                objectFit: "contain"
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                ))}
                                                            </Grid>
                                                        </Box>
                                                    </CardBody>
                                                </Card>
                                            </TabPanel>
                                        )}
                                    </TabPanels>
                                </Tabs>
                                <Modal
                                    isOpen={!!imagePreviewUrl}
                                    onClose={() => setImagePreviewUrl(null)}
                                    size="6xl"
                                    scrollBehavior="inside"
                                >
                                    <ModalOverlay />
                                    <ModalContent maxH="90vh" bg={cardBg}>
                                        <ModalCloseButton />
                                        <ModalBody p={0} display="block">
                                            {imagePreviewUrl && (
                                                <img
                                                    src={imagePreviewUrl}
                                                    alt=""
                                                    style={{
                                                        width: "100%",
                                                        height: "auto",
                                                        display: "block",
                                                        verticalAlign: "top"
                                                    }}
                                                />
                                            )}
                                        </ModalBody>
                                    </ModalContent>
                                </Modal>
                                {(isLoadingPrintAit && isLoadingPrintNa && isLoadingPrintNp &&
                                    <Box
                                        position="fixed"
                                        top="0"
                                        left="0"
                                        right="0"
                                        bottom="0"
                                        bg="rgba(255, 255, 255, 0.6)"
                                        display="flex"
                                        justifyContent="center"
                                        alignItems="center"
                                        zIndex="overlay"
                                    >
                                        <Loading maxH={{ sm: "100px", md: "350px" }} />
                                    </Box>
                                )}
                            </>
                        ) : (
                            <Loading maxH={{ sm: "100px", md: "350px" }} />
                        )
                    }
                    <Flex justifyContent="flex-start">
                        <Button
                            variant="light"
                            fontSize="sm"
                            alignSelf="flex-end"
                            mt="24px"
                            w="125px"
                            onClick={() => navigate(-1)}
                        >
                            {t("system.actions.back")}
                        </Button>
                    </Flex>
                </CardBody>
            </Card>
        </Flex >
    )
}

export default TrafficViolationDetails;